import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Eye, EyeOff, AlertCircle, FileText, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/tableau-de-bord');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      <div className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-zinc-900 mb-2"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('register.title')}
            </h1>
            <p className="text-zinc-600">
              {t('register.subtitle')}
            </p>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-8" data-testid="register-form">
            {/* Benefits */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-2">{t('register.included_free')}</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2 text-sm text-blue-800">
                  <CheckCircle className="h-4 w-4" />
                  {t('register.feature_1')}
                </li>
                <li className="flex items-center gap-2 text-sm text-blue-800">
                  <CheckCircle className="h-4 w-4" />
                  {t('register.feature_2')}
                </li>
                <li className="flex items-center gap-2 text-sm text-blue-800">
                  <CheckCircle className="h-4 w-4" />
                  {t('register.feature_3')}
                </li>
              </ul>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="register-error">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-zinc-700">{t('register.full_name')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('register.name_placeholder')}
                  required
                  className="mt-2 border-zinc-200 focus:border-blue-600 focus:ring-blue-600"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-zinc-700">{t('register.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('register.email_placeholder')}
                  required
                  className="mt-2 border-zinc-200 focus:border-blue-600 focus:ring-blue-600"
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-700">{t('register.password')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('register.password_placeholder')}
                    required
                    minLength={6}
                    className="border-zinc-200 focus:border-blue-600 focus:ring-blue-600 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-3"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin inline" />{t('register.loading')}</>
                ) : t('register.submit')}
              </Button>
            </form>

            <p className="mt-4 text-xs text-zinc-500 text-center">
              {t('register.terms_text')}{' '}
              <Link to="/conditions" className="underline">{t('register.terms_link')}</Link>
              {' '}{t('register.and')}{' '}
              <Link to="/confidentialite" className="underline">{t('register.privacy_link')}</Link>.
            </p>

            <div className="mt-6 text-center text-sm text-zinc-600">
              {t('register.already_account')}{' '}
              <Link to="/connexion" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
                {t('register.sign_in')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterPage;
